import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export const useTrialGuard = (toolName) => {
  const { user, loading } = useAuth();
  const [trialBlocked, setTrialBlocked] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Check if user is PRO by querying profiles table
  const checkIsPro = useCallback(async () => {
    if (!user) return false;
    try {
      const { data, error } = await supabase?.from('profiles')?.select('plan')?.eq('id', user?.id)?.single();
      if (error) return false;
      return data?.plan === 'pro';
    } catch {
      return false;
    }
  }, [user]);

  // Get or create tool_usage record — uses upsert to avoid race conditions
  const getOrCreateUsage = useCallback(async () => {
    if (!user) return null;
    try {
      // Step 1: Try to fetch existing record
      const { data: existing, error: fetchError } = await supabase
        ?.from('tool_usage')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tool_name', toolName)
        ?.maybeSingle();

      // If fetch succeeded and record exists, return it
      if (!fetchError && existing) {
        return existing;
      }

      // If record doesn't exist (null result or PGRST116), create it
      if (!fetchError && !existing) {
        const { data: created, error: createError } = await supabase
          ?.from('tool_usage')
          ?.insert({
            user_id: user?.id,
            tool_name: toolName,
            messages_used: 0,
            trial_limit: 2
          })
          ?.select()
          ?.single();

        if (createError) {
          // Insert failed (e.g. duplicate from race condition) — try fetching again
          const { data: retry } = await supabase
            ?.from('tool_usage')
            ?.select('*')
            ?.eq('user_id', user?.id)
            ?.eq('tool_name', toolName)
            ?.maybeSingle();
          return retry || null;
        }
        return created;
      }

      // fetchError with PGRST116 = no rows (old Supabase behavior)
      if (fetchError?.code === 'PGRST116') {
        const { data: created, error: createError } = await supabase
          ?.from('tool_usage')
          ?.insert({
            user_id: user?.id,
            tool_name: toolName,
            messages_used: 0,
            trial_limit: 2
          })
          ?.select()
          ?.single();

        if (createError) {
          const { data: retry } = await supabase
            ?.from('tool_usage')
            ?.select('*')
            ?.eq('user_id', user?.id)
            ?.eq('tool_name', toolName)
            ?.maybeSingle();
          return retry || null;
        }
        return created;
      }

      return null;
    } catch {
      return null;
    }
  }, [user, toolName]);

  // Call BEFORE AI request — returns true if allowed, false if blocked
  const checkBeforeAI = useCallback(async () => {
    if (loading) return false;
    if (!user) return false;

    // PRO users always allowed
    const isPro = await checkIsPro();
    if (isPro) {
      setTrialBlocked(false);
      return true;
    }

    const usage = await getOrCreateUsage();

    // If we can't determine usage, fail open (allow the call)
    if (!usage) {
      setTrialBlocked(false);
      return true;
    }

    const used = usage?.messages_used ?? 0;
    const limit = usage?.trial_limit ?? 2;

    // Block only when messages_used >= trial_limit
    if (used >= limit) {
      setTrialBlocked(true);
      return false;
    }

    // Allow the call
    setTrialBlocked(false);
    return true;
  }, [user, loading, checkIsPro, getOrCreateUsage]);

  // Call AFTER successful AI response — increments messages_used
  const incrementAfterAI = useCallback(async () => {
    if (!user) return;

    // PRO users don't need increment tracking
    const isPro = await checkIsPro();
    if (isPro) return;

    try {
      const { data: existing } = await supabase
        ?.from('tool_usage')
        ?.select('id, messages_used')
        ?.eq('user_id', user?.id)
        ?.eq('tool_name', toolName)
        ?.maybeSingle();

      if (existing) {
        await supabase
          ?.from('tool_usage')
          ?.update({ messages_used: existing?.messages_used + 1 })
          ?.eq('id', existing?.id);
      }
    } catch {
      // Silent fail — don't break the UX
    }
  }, [user, toolName, checkIsPro]);

  // Handle ASSINAR PRO button click
  const handleAssinarPro = useCallback(async () => {
    if (!user) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const country = Intl.DateTimeFormat()?.resolvedOptions()?.locale?.split('-')?.[1];
      const { data, error } = await supabase?.functions?.invoke('create-checkout', {
        body: { user_id: user?.id, email: user?.email, country: country }
      });
      if (error) throw new Error(error.message || 'Erro ao conectar com o servidor.');
      const url = data?.url || data?.checkout_url;
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('URL de pagamento não recebida. Tente novamente.');
      }
    } catch (err) {
      setCheckoutError(err?.message || 'Ocorreu um erro ao iniciar o pagamento. Tente novamente.');
    } finally {
      setCheckoutLoading(false);
    }
  }, [user]);

  return {
    trialBlocked,
    setTrialBlocked,
    checkoutLoading,
    checkoutError,
    setCheckoutError,
    checkBeforeAI,
    incrementAfterAI,
    handleAssinarPro
  };
};

export default useTrialGuard;
